import { useEffect, useState } from 'react';

interface RegisteredUser {
  id: string;
  username: string;
  email: string;
  bio: string;
  role: string;
  joinDate: string;
  postCount: number;
}

interface AdminPanelProps {
  onClose: () => void;
}

export default function AdminPanel({ onClose }: AdminPanelProps) {
  const [users, setUsers] = useState<RegisteredUser[]>([]);

  useEffect(() => {
    const emailList: string[] = JSON.parse(localStorage.getItem('bbs_user_list') || '[]');
    const loadedUsers: RegisteredUser[] = emailList.map((email) => {
      try {
        const u = JSON.parse(localStorage.getItem(`bbs_user_${email}`) || '{}');
        return {
          id: u.id || email,
          username: u.username || email,
          email,
          bio: u.bio || '',
          role: u.role || 'member',
          joinDate: u.joinDate || '',
          postCount: u.postCount || 0,
        };
      } catch {
        return { id: email, username: email, email, bio: '', role: 'member', joinDate: '', postCount: 0 };
      }
    });
    setUsers(loadedUsers);
  }, []);

  return (
    <div className="fixed inset-0 z-[99996] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#2E1065] to-[#4C1D95] rounded-t-2xl">
          <div>
            <h2 className="font-black text-white text-lg flex items-center gap-2">
              <i className="ri-admin-line text-[#F59E0B]" />
              管理员面板 — 注册用户管理
            </h2>
            <p className="text-white/60 text-xs mt-0.5">共 {users.length} 位注册用户</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 p-6 border-b border-gray-100">
          {[
            { label: '总注册用户', value: users.length, icon: 'ri-user-line', color: '#7C3AED' },
            { label: '今日新增', value: users.filter(u => u.joinDate === new Date().toISOString().slice(0, 10)).length, icon: 'ri-user-add-line', color: '#059669' },
            { label: '管理员账号', value: '13810001256@126.com', icon: 'ri-shield-star-line', color: '#F59E0B', small: true },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: `${s.color}20` }}>
                  <i className={`${s.icon} text-sm`} style={{ color: s.color }} />
                </div>
                <span className="text-gray-500 text-xs">{s.label}</span>
              </div>
              <div className={`font-black text-gray-900 ${s.small ? 'text-xs break-all' : 'text-2xl'}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto p-6">
          {users.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-user-search-line text-4xl mb-3 block" />
              <p className="text-sm">暂无注册用户</p>
              <p className="text-xs mt-1">用户注册后将在此处显示</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="flex items-center gap-4 p-4 border border-gray-100 rounded-xl hover:border-[#7C3AED]/20 transition-all">
                  <div className="w-10 h-10 rounded-full bg-[#7C3AED]/20 flex items-center justify-center font-black text-[#7C3AED] text-base shrink-0">
                    {user.username[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-900 text-sm">{user.username}</span>
                      <span className="px-1.5 py-0.5 text-[10px] rounded bg-[#7C3AED]/10 text-[#7C3AED] font-bold">{user.role}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 truncate">{user.email}</div>
                    {user.bio && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{user.bio}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-gray-400">注册日期</div>
                    <div className="text-xs font-semibold text-gray-700">{user.joinDate || '未知'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="px-6 py-4 border-t border-gray-100 bg-amber-50 rounded-b-2xl">
          <p className="text-amber-700 text-xs flex items-start gap-1.5">
            <i className="ri-information-line mt-0.5 shrink-0" />
            当前注册用户数据存储在浏览器本地。如需持久化数据库存储，请联系开发团队接入 Supabase 后端。
          </p>
        </div>
      </div>
    </div>
  );
}
